import { getCategoryIdFromName, getMasterCategoryList } from 'owa-categories';
import { createMailCategoryFolderTableQuery } from 'owa-mail-list-actions';
import ViewFilter from 'owa-service/lib/contract/ViewFilter';
import { TableQuery } from 'owa-mail-list-store';

const FILTER_FOLDER_ID_BUILDER = '#';

export interface FilteredFolderFilterProperties {
    filterType: ViewFilter;
    filterValue: string;
}

/**
 * Gets the filteredFolderId given the filterType and filterValue
 * @param filterFolderFilterProps required to form the filtered folder id
 * @return the folder id for the filtered folder in the folderType#folderValue format
 * E.g. for a category folder filter the viewfilter is "UserCategory" and filter value is category name
 * So in case of category folder the filtered folder id will be "UserCategory#foo"
 */
export default function getFilteredFolderId(
    filterFolderFilterProps: FilteredFolderFilterProperties
) {
    if (filterFolderFilterProps.filterType != 'UserCategory') {
        throw new Error(
            "getFilteredFolderId should only be called for 'UserCategory' viewfilter type"
        );
    }

    return (
        filterFolderFilterProps.filterType +
        FILTER_FOLDER_ID_BUILDER +
        filterFolderFilterProps.filterValue
    );
}

/**
 * Gets the filter folder type and value given the folderId
 * @param folderId folderId from which to derive the the filteredFolderFilterProperties
 */
export function getFilterTypeAndValueForFilteredFolder(
    folderId: string
): FilteredFolderFilterProperties {
    const filteredFolderProperties = folderId.split(FILTER_FOLDER_ID_BUILDER);

    if (filteredFolderProperties.length == 2) {
        return {
            filterType: filteredFolderProperties[0] as ViewFilter,
            filterValue: filteredFolderProperties[1],
        };
    }

    return null;
}

/**
 * Gets the table query for the filtered folder
 * @param filteredFolderFilterProps the properties for the filtered folder
 */
export function getTableQueryForFilteredFolder(
    filteredFolderFilterProps: FilteredFolderFilterProperties
): TableQuery {
    if (filteredFolderFilterProps.filterType != 'UserCategory') {
        throw new Error(
            "getQueryTypeForFilteredFolder should only be called for 'UserCategory' viewfilter type"
        );
    }

    const categoryId = getCategoryIdFromName(
        filteredFolderFilterProps.filterValue,
        getMasterCategoryList()
    );

    // The category might have been deleted from master category list when trying to prefetch
    // VSO 30372: update FUF list when a folder/category is deleted
    if (!categoryId) {
        return null;
    }

    return createMailCategoryFolderTableQuery(categoryId);
}

/**
 * Gets the flag indicating whether the folderId is for a filtered folder
 * @param fufFolderId the folder id from frequently used folders list
 */
export function isFilteredFolder(fufFolderId: string): boolean {
    const folderFilterTypes = ['SystemCategory', 'Hashtag', 'UserCategory'];
    return folderFilterTypes.reduce(
        (isMatch, folderToMatch) =>
            isMatch || fufFolderId.slice(0, folderToMatch.length) == folderToMatch,
        false
    );
}

/**
 * Gets a flag indicating whether to prefetch the filtered folder
 * @param filterType the filter type for the filtered folder
 */
export function shouldPrefetchFilteredFolder(filterType: string): boolean {
    return filterType == 'UserCategory';
}
